import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { EventsClient } from '../events-client.js';
import Logger from '../utils/logger.js';
import {
    createUserAdminMenuSchema,
    readUserAdminSchemaButtonLabel,
    readUserAdminSchemaInputPlaceholder,
    USER_ADMIN_MENU_ACTIONS,
    USER_ADMIN_MENU_LABELS,
} from './schemas/user-admin-menu-schema.js';

const MODULE = 'UserAdminSubmenu';

const CSS = `
        .mm-user-admin-grid {
            display: grid;
            gap: 0.75rem;
            grid-template-columns: 1fr;
            margin-top: 0.5rem;
        }

        .mm-user-admin-card {
            border: 1px solid #00d9ff;
            border-radius: 10px;
            padding: 0.75rem;
            background: rgba(10, 15, 30, 0.45);
            text-align: left;
        }

        .mm-user-admin-card h3 {
            margin: 0 0 0.55rem 0;
            color: #b8e3f0;
            font-size: clamp(0.85rem, 2.3vw, 1rem);
            text-transform: uppercase;
            letter-spacing: 0.06em;
        }

        .mm-user-admin-row {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
            margin-bottom: 0.6rem;
        }

        .mm-user-admin-row label {
            color: #b8e3f0;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
        }

        .mm-user-admin-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.4rem;
        }

        .mm-user-admin-note {
            color: rgba(184, 227, 240, 0.8);
            font-size: clamp(0.74rem, 2vw, 0.85rem);
            margin-top: 0.25rem;
        }

        .mm-user-admin-list {
            border: 1px solid rgba(0, 217, 255, 0.35);
            border-radius: 8px;
            max-height: 240px;
            overflow-y: auto;
        }

        .mm-user-admin-list-row {
            padding: 0.45rem 0.6rem;
            border-bottom: 1px solid rgba(0, 217, 255, 0.15);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
        }

        .mm-user-admin-list-row:last-child {
            border-bottom: none;
        }

        .mm-user-admin-list-row:hover {
            background: rgba(0, 217, 255, 0.08);
        }

        .mm-user-admin-list-row.selected {
            background: rgba(0, 217, 255, 0.18);
        }

        .mm-user-admin-username {
            color: #00ffff;
            font-weight: 700;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
        }

        .mm-user-admin-displayname {
            color: #e0f4ff;
            font-size: clamp(0.74rem, 1.9vw, 0.84rem);
            opacity: 0.9;
        }

        .mm-user-admin-admin-badge {
            color: #00ff9d;
            border: 1px solid rgba(0, 255, 157, 0.45);
            border-radius: 999px;
            padding: 0.1rem 0.4rem;
            font-size: 0.72rem;
            font-weight: 700;
            white-space: nowrap;
        }

        .mm-user-admin-entertainer-badge {
            color: #ffd166;
            border: 1px solid rgba(255, 209, 102, 0.45);
            border-radius: 999px;
            padding: 0.1rem 0.4rem;
            font-size: 0.72rem;
            font-weight: 700;
            white-space: nowrap;
        }

        .mm-user-admin-token-balance {
            color: #00ffff;
            font-weight: 700;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            margin-top: 0.2rem;
        }

        .mm-user-admin-status {
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
            min-height: 1.2em;
            margin-top: 0.35rem;
        }

        .mm-user-admin-status.success {
            color: #00ff9d;
            text-shadow: 0 0 6px rgba(0, 255, 157, 0.5);
        }

        .mm-user-admin-status.error {
            color: #ff4444;
            text-shadow: 0 0 6px rgba(255, 68, 68, 0.5);
        }

        .mm-user-admin-hidden-username {
            position: absolute;
            left: -10000px;
            width: 1px;
            height: 1px;
            opacity: 0;
            pointer-events: none;
        }

        @media (min-width: 960px) {
            .mm-user-admin-grid {
                grid-template-columns: 1fr 1fr;
            }
        }
`;

const MM_USER_ADMIN_HTML = `
        <div id="menu-modal-user-admin" class="menu-modal-content">
            <h2>User Admin</h2>
            <div class="mm-user-admin-grid">
                <div class="mm-user-admin-card">
                    <h3>User Form</h3>
                    <form id="mm-user-admin-form" onsubmit="return false;" autocomplete="on">
                        <div class="mm-user-admin-row">
                            <label for="mm-user-admin-username">Username</label>
                            <input id="mm-user-admin-username" class="mm-text-input" type="text" autocomplete="username"
                                placeholder="username" onkeydown="event.stopPropagation()" />
                        </div>
                        <div class="mm-user-admin-row">
                            <label for="mm-user-admin-display-name">Display Name</label>
                            <input id="mm-user-admin-display-name" class="mm-text-input" type="text" autocomplete="name"
                                placeholder="display name" onkeydown="event.stopPropagation()" />
                        </div>
                        <div id="mm-user-admin-create-password-row" class="mm-user-admin-row">
                            <label for="mm-user-admin-create-password">Password (new user only)</label>
                            <input id="mm-user-admin-create-password" class="mm-text-input" type="password" autocomplete="new-password"
                                placeholder="minimum 8 characters" onkeydown="event.stopPropagation()" />
                        </div>
                        <div class="mm-user-admin-row" style="flex-direction:row; align-items:center; gap:0.5rem;">
                            <input id="mm-user-admin-is-admin" type="checkbox" />
                            <label for="mm-user-admin-is-admin" style="margin:0;">User Administration Privilege</label>
                        </div>
                        <div class="mm-user-admin-row" style="flex-direction:row; align-items:center; gap:0.5rem;">
                            <input id="mm-user-admin-is-entertainer" type="checkbox" />
                            <label for="mm-user-admin-is-entertainer" style="margin:0;">Entertainer Privilege</label>
                        </div>
                        <div class="mm-user-admin-row">
                            <label>Selected User Token Balance</label>
                            <div id="mm-user-admin-token-balance" class="mm-user-admin-token-balance">-</div>
                        </div>
                        <div class="mm-user-admin-actions">
                            <button id="mm-user-admin-create-button" class="mm-submit-button" type="button">Create User</button>
                            <button id="mm-user-admin-update-button" class="mm-submit-button" type="button" style="display:none;" disabled>Update Selected</button>
                        </div>
                    </form>
                    <div id="mm-user-admin-form-status" class="mm-user-admin-status"></div>
                    <div class="mm-user-admin-note">Select a user from the list to update username/display name/admin privilege. Password updates are handled separately.</div>
                </div>

                <div class="mm-user-admin-card">
                    <h3>Existing Users</h3>
                    <div id="mm-user-admin-user-list" class="mm-user-admin-list"></div>
                    <div class="mm-user-admin-actions" style="margin-top:0.55rem;">
                        <button id="mm-user-admin-refresh-button" class="mm-submit-button" type="button">Refresh List</button>
                    </div>
                </div>

                <div class="mm-user-admin-card" style="grid-column: 1 / -1;">
                    <h3>Set Selected User Password</h3>
                    <form id="mm-user-admin-password-form" onsubmit="return false;" autocomplete="on">
                        <input id="mm-user-admin-password-username" class="mm-user-admin-hidden-username" type="text"
                               autocomplete="username" tabindex="-1" aria-hidden="true" />
                        <div class="mm-user-admin-row">
                            <label for="mm-user-admin-new-password">New Password</label>
                            <input id="mm-user-admin-new-password" class="mm-text-input" type="password" autocomplete="new-password"
                                placeholder="minimum 8 characters" onkeydown="event.stopPropagation()" />
                        </div>
                        <div class="mm-user-admin-actions">
                            <button id="mm-user-admin-set-password-button" class="mm-submit-button" type="submit" disabled>Set Password</button>
                        </div>
                    </form>
                    <div class="mm-user-admin-row" style="margin-top:0.55rem;">
                        <label for="mm-user-admin-deposit-amount">One-Time Token Deposit</label>
                        <input id="mm-user-admin-deposit-amount" class="mm-text-input" type="number" min="1"
                            placeholder="token amount" onkeydown="event.stopPropagation()" />
                    </div>
                    <div class="mm-user-admin-actions">
                        <button id="mm-user-admin-deposit-button" class="mm-submit-button" type="button" style="display:none;" disabled>Deposit Tokens</button>
                    </div>
                    <div id="mm-user-admin-password-status" class="mm-user-admin-status"></div>
                </div>
            </div>
        </div>
`;

interface AdminUser {
    id: string;
    username: string;
    display_name: string | null;
    is_user_admin: boolean;
    is_entertainer: boolean;
    token_balance: number;
    created_at: string;
    updated_at: string;
}

export class UserAdminSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'UserAdminSubmenuDH';

    private styleEl: HTMLStyleElement | null = null;
    private selectedUserId: string | null = null;
    private users: AdminUser[] = [];
    private marketplaceFieldsSupported = true;

    updateConditionals(): void {
        // No periodic refresh required.
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        this.styleEl = document.createElement('style');
        this.styleEl.innerText = CSS;
        document.head.appendChild(this.styleEl);

        containerDiv.innerHTML = MM_USER_ADMIN_HTML;
        const userAdminSchema = createUserAdminMenuSchema();
        this.applySchemaLabels(containerDiv, userAdminSchema);

        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            Logger.error(MODULE, 'EventsClient singleton not available.');
            return;
        }

        const token = eventsClient.getAuthToken();
        if (!token) {
            this.setFormStatus(USER_ADMIN_MENU_LABELS.notAuthenticated, 'error');
            return;
        }

        const createBtn = document.getElementById('mm-user-admin-create-button') as HTMLButtonElement | null;
        const updateBtn = document.getElementById('mm-user-admin-update-button') as HTMLButtonElement | null;
        const depositBtn = document.getElementById('mm-user-admin-deposit-button') as HTMLButtonElement | null;
        const refreshBtn = document.getElementById('mm-user-admin-refresh-button') as HTMLButtonElement | null;
        const passwordForm = document.getElementById('mm-user-admin-password-form') as HTMLFormElement | null;

        createBtn?.addEventListener('click', () => {
            if (this.selectedUserId) {
                this.clearSelection();
                this.setFormStatus('Form cleared.', 'success');
            } else {
                this.createUser(eventsClient, token);
            }
        });
        updateBtn?.addEventListener('click', () => this.updateSelectedUser(eventsClient, token));
        depositBtn?.addEventListener('click', () => this.depositSelectedUserTokens(eventsClient, token));
        refreshBtn?.addEventListener('click', () => this.loadUsers(eventsClient, token));

        passwordForm?.addEventListener('submit', (event) => {
            event.preventDefault();
            this.setSelectedUserPassword(eventsClient, token);
        });

        this.clearSelection();
        this.loadUsers(eventsClient, token);
    }

    hideContent(): void {
        if (this.styleEl && this.styleEl.parentNode) {
            this.styleEl.parentNode.removeChild(this.styleEl);
            this.styleEl = null;
        }
        this.selectedUserId = null;
        this.users = [];
    }

    private applySchemaLabels(containerDiv: HTMLDivElement, userAdminSchema: ReturnType<typeof createUserAdminMenuSchema>): void {
        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = userAdminSchema.title;
        }

        const cardTitles = containerDiv.querySelectorAll('.mm-user-admin-card h3');
        if (cardTitles[0]) cardTitles[0].textContent = 'User Form';
        if (cardTitles[1]) cardTitles[1].textContent = 'Existing Users';
        if (cardTitles[2]) cardTitles[2].textContent = 'Set Selected User Password';

        const usernameLabel = containerDiv.querySelector('label[for="mm-user-admin-username"]');
        if (usernameLabel) usernameLabel.textContent = USER_ADMIN_MENU_LABELS.username;

        const usernameInput = document.getElementById('mm-user-admin-username') as HTMLInputElement | null;
        if (usernameInput) {
            usernameInput.placeholder = readUserAdminSchemaInputPlaceholder(
                userAdminSchema,
                'user-admin-username-input',
                USER_ADMIN_MENU_LABELS.usernamePlaceholder,
            );
        }

        const displayNameLabel = containerDiv.querySelector('label[for="mm-user-admin-display-name"]');
        if (displayNameLabel) displayNameLabel.textContent = USER_ADMIN_MENU_LABELS.displayName;

        const displayNameInput = document.getElementById('mm-user-admin-display-name') as HTMLInputElement | null;
        if (displayNameInput) {
            displayNameInput.placeholder = USER_ADMIN_MENU_LABELS.displayNamePlaceholder;
        }

        const createPasswordLabel = containerDiv.querySelector('label[for="mm-user-admin-create-password"]');
        if (createPasswordLabel) createPasswordLabel.textContent = USER_ADMIN_MENU_LABELS.passwordNewUserLong;

        const createPasswordInput = document.getElementById('mm-user-admin-create-password') as HTMLInputElement | null;
        if (createPasswordInput) {
            createPasswordInput.placeholder = USER_ADMIN_MENU_LABELS.passwordMinPlaceholder;
        }

        const createBtn = document.getElementById('mm-user-admin-create-button') as HTMLButtonElement | null;
        if (createBtn) {
            createBtn.textContent = readUserAdminSchemaButtonLabel(
                userAdminSchema,
                USER_ADMIN_MENU_ACTIONS.createUser,
                USER_ADMIN_MENU_LABELS.createUser,
            );
        }

        const updateBtn = document.getElementById('mm-user-admin-update-button') as HTMLButtonElement | null;
        if (updateBtn) {
            updateBtn.textContent = USER_ADMIN_MENU_LABELS.updateSelected;
        }

        const isEntertainerLabel = containerDiv.querySelector('label[for="mm-user-admin-is-entertainer"]');
        if (isEntertainerLabel) isEntertainerLabel.textContent = USER_ADMIN_MENU_LABELS.isEntertainer;

        const depositAmountLabel = containerDiv.querySelector('label[for="mm-user-admin-deposit-amount"]');
        if (depositAmountLabel) depositAmountLabel.textContent = USER_ADMIN_MENU_LABELS.depositAmount;

        const depositAmountInput = document.getElementById('mm-user-admin-deposit-amount') as HTMLInputElement | null;
        if (depositAmountInput) {
            depositAmountInput.placeholder = readUserAdminSchemaInputPlaceholder(
                userAdminSchema,
                'user-admin-deposit-amount-input',
                USER_ADMIN_MENU_LABELS.depositAmountPlaceholder,
            );
        }

        const depositBtn = document.getElementById('mm-user-admin-deposit-button') as HTMLButtonElement | null;
        if (depositBtn) {
            depositBtn.textContent = readUserAdminSchemaButtonLabel(
                userAdminSchema,
                USER_ADMIN_MENU_ACTIONS.depositTokens,
                USER_ADMIN_MENU_LABELS.depositTokens,
            );
        }

        const refreshBtn = document.getElementById('mm-user-admin-refresh-button') as HTMLButtonElement | null;
        if (refreshBtn) {
            refreshBtn.textContent = readUserAdminSchemaButtonLabel(
                userAdminSchema,
                USER_ADMIN_MENU_ACTIONS.refreshList,
                USER_ADMIN_MENU_LABELS.refreshList,
            );
        }

        const setPasswordBtn = document.getElementById('mm-user-admin-set-password-button') as HTMLButtonElement | null;
        if (setPasswordBtn) {
            setPasswordBtn.textContent = readUserAdminSchemaButtonLabel(
                userAdminSchema,
                USER_ADMIN_MENU_ACTIONS.setPassword,
                USER_ADMIN_MENU_LABELS.setPassword,
            );
        }

        const newPasswordInput = document.getElementById('mm-user-admin-new-password') as HTMLInputElement | null;
        if (newPasswordInput) {
            newPasswordInput.placeholder = USER_ADMIN_MENU_LABELS.passwordMinPlaceholder;
        }
    }

    private async loadUsers(eventsClient: EventsClient, token: string): Promise<void> {
        const listEl = document.getElementById('mm-user-admin-user-list');
        if (listEl) {
            listEl.innerHTML = '<div class="mm-user-admin-list-row">Loading users...</div>';
        }

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                let msg = (data as { error?: string }).error ?? USER_ADMIN_MENU_LABELS.failedToLoad;
                if (resp.status === 404) {
                    msg = 'User admin endpoint not found on server. Deploy/restart REST server with /api/admin/users support.';
                }
                this.setFormStatus(msg, 'error');
                if (listEl) {
                    listEl.innerHTML = `<div class="mm-user-admin-list-row">${msg}</div>`;
                }
                return;
            }

            this.users = (data as AdminUser[]) || [];
            this.marketplaceFieldsSupported = this.users.length === 0 || this.users.some((user) =>
                typeof (user as Partial<AdminUser>).is_entertainer === 'boolean'
                && typeof (user as Partial<AdminUser>).token_balance === 'number'
            );
            if (!this.marketplaceFieldsSupported) {
                this.setFormStatus(
                    'Server response is missing marketplace fields (is_entertainer/token_balance). Deploy latest rest-server + DB migration 004.',
                    'error',
                );
            }
            this.renderUserList();

            if (this.selectedUserId) {
                const selected = this.users.find((u) => u.id === this.selectedUserId);
                if (selected) {
                    this.populateFormForSelection(selected);
                    this.highlightSelectedRow(selected.id);
                } else {
                    this.clearSelection();
                }
            }
        } catch (err) {
            Logger.error(MODULE, 'Error loading admin user list', err);
            this.setFormStatus(USER_ADMIN_MENU_LABELS.failedToLoad, 'error');
            if (listEl) {
                listEl.innerHTML = `<div class="mm-user-admin-list-row">${USER_ADMIN_MENU_LABELS.failedToLoad}</div>`;
            }
        }
    }

    private renderUserList(): void {
        const listEl = document.getElementById('mm-user-admin-user-list');
        if (!listEl) return;

        if (this.users.length === 0) {
            listEl.innerHTML = '<div class="mm-user-admin-list-row">No users found.</div>';
            return;
        }

        listEl.innerHTML = '';

        for (const user of this.users) {
            const row = document.createElement('div');
            row.className = 'mm-user-admin-list-row';
            row.dataset['id'] = user.id;

            const left = document.createElement('div');
            const userName = document.createElement('div');
            userName.className = 'mm-user-admin-username';
            userName.textContent = user.username;
            const displayName = document.createElement('div');
            displayName.className = 'mm-user-admin-displayname';
            displayName.textContent = user.display_name ?? '—';
            left.appendChild(userName);
            left.appendChild(displayName);

            const right = document.createElement('div');
            if (user.is_user_admin) {
                const badge = document.createElement('span');
                badge.className = 'mm-user-admin-admin-badge';
                badge.textContent = 'ADMIN';
                right.appendChild(badge);
            }
            if (user.is_entertainer) {
                const badge = document.createElement('span');
                badge.className = 'mm-user-admin-entertainer-badge';
                badge.textContent = 'ENTERTAINER';
                right.appendChild(badge);
            }

            row.appendChild(left);
            row.appendChild(right);
            row.addEventListener('click', () => {
                this.selectedUserId = user.id;
                this.populateFormForSelection(user);
                this.highlightSelectedRow(user.id);
                this.setFormStatus(`Selected user: ${user.username}`, 'success');
            });

            listEl.appendChild(row);
        }

        if (this.selectedUserId) {
            this.highlightSelectedRow(this.selectedUserId);
        }
    }

    private highlightSelectedRow(userId: string): void {
        const listEl = document.getElementById('mm-user-admin-user-list');
        if (!listEl) return;

        for (const row of Array.from(listEl.querySelectorAll('.mm-user-admin-list-row'))) {
            row.classList.remove('selected');
            if ((row as HTMLElement).dataset['id'] === userId) {
                row.classList.add('selected');
            }
        }
    }

    private populateFormForSelection(user: AdminUser): void {
        const usernameEl = document.getElementById('mm-user-admin-username') as HTMLInputElement | null;
        const displayNameEl = document.getElementById('mm-user-admin-display-name') as HTMLInputElement | null;
        const isAdminEl = document.getElementById('mm-user-admin-is-admin') as HTMLInputElement | null;
        const isEntertainerEl = document.getElementById('mm-user-admin-is-entertainer') as HTMLInputElement | null;
        const passwordRowEl = document.getElementById('mm-user-admin-create-password-row') as HTMLDivElement | null;
        const createPasswordEl = document.getElementById('mm-user-admin-create-password') as HTMLInputElement | null;
        const updateBtn = document.getElementById('mm-user-admin-update-button') as HTMLButtonElement | null;
        const depositBtn = document.getElementById('mm-user-admin-deposit-button') as HTMLButtonElement | null;
        const setPasswordBtn = document.getElementById('mm-user-admin-set-password-button') as HTMLButtonElement | null;
        const hiddenUsernameEl = document.getElementById('mm-user-admin-password-username') as HTMLInputElement | null;
        const createBtn = document.getElementById('mm-user-admin-create-button') as HTMLButtonElement | null;
        const tokenBalanceEl = document.getElementById('mm-user-admin-token-balance');

        if (usernameEl) usernameEl.value = user.username;
        if (displayNameEl) displayNameEl.value = user.display_name ?? '';
        if (isAdminEl) isAdminEl.checked = !!user.is_user_admin;
        if (isEntertainerEl) isEntertainerEl.checked = !!user.is_entertainer;
        if (createPasswordEl) createPasswordEl.value = '';
        if (passwordRowEl) passwordRowEl.style.display = 'none';
        if (updateBtn) {
            updateBtn.disabled = false;
            updateBtn.style.display = '';
        }
        if (depositBtn) {
            depositBtn.disabled = false;
            depositBtn.style.display = '';
        }
        if (setPasswordBtn) setPasswordBtn.disabled = false;
        if (hiddenUsernameEl) hiddenUsernameEl.value = user.username;
        if (createBtn) createBtn.textContent = USER_ADMIN_MENU_LABELS.clearForm;
        if (tokenBalanceEl) tokenBalanceEl.textContent = String(Number(user.token_balance ?? 0));
    }

    private clearSelection(): void {
        this.selectedUserId = null;

        const usernameEl = document.getElementById('mm-user-admin-username') as HTMLInputElement | null;
        const displayNameEl = document.getElementById('mm-user-admin-display-name') as HTMLInputElement | null;
        const isAdminEl = document.getElementById('mm-user-admin-is-admin') as HTMLInputElement | null;
        const isEntertainerEl = document.getElementById('mm-user-admin-is-entertainer') as HTMLInputElement | null;
        const passwordRowEl = document.getElementById('mm-user-admin-create-password-row') as HTMLDivElement | null;
        const createPasswordEl = document.getElementById('mm-user-admin-create-password') as HTMLInputElement | null;
        const updateBtn = document.getElementById('mm-user-admin-update-button') as HTMLButtonElement | null;
        const depositBtn = document.getElementById('mm-user-admin-deposit-button') as HTMLButtonElement | null;
        const setPasswordBtn = document.getElementById('mm-user-admin-set-password-button') as HTMLButtonElement | null;
        const newPasswordEl = document.getElementById('mm-user-admin-new-password') as HTMLInputElement | null;
        const hiddenUsernameEl = document.getElementById('mm-user-admin-password-username') as HTMLInputElement | null;
        const createBtn = document.getElementById('mm-user-admin-create-button') as HTMLButtonElement | null;
        const depositAmountEl = document.getElementById('mm-user-admin-deposit-amount') as HTMLInputElement | null;
        const tokenBalanceEl = document.getElementById('mm-user-admin-token-balance');

        if (usernameEl) usernameEl.value = '';
        if (displayNameEl) displayNameEl.value = '';
        if (isAdminEl) isAdminEl.checked = false;
        if (isEntertainerEl) isEntertainerEl.checked = false;
        if (passwordRowEl) passwordRowEl.style.display = 'flex';
        if (createPasswordEl) createPasswordEl.value = '';
        if (updateBtn) {
            updateBtn.disabled = true;
            updateBtn.style.display = 'none';
        }
        if (depositBtn) {
            depositBtn.disabled = true;
            depositBtn.style.display = 'none';
        }
        if (setPasswordBtn) setPasswordBtn.disabled = true;
        if (newPasswordEl) newPasswordEl.value = '';
        if (hiddenUsernameEl) hiddenUsernameEl.value = '';
        if (createBtn) createBtn.textContent = USER_ADMIN_MENU_LABELS.createUser;
        if (depositAmountEl) depositAmountEl.value = '';
        if (tokenBalanceEl) tokenBalanceEl.textContent = USER_ADMIN_MENU_LABELS.tokenBalanceUnknown;

        this.setPasswordStatus('', '');
        this.highlightSelectedRow('');
    }

    private async createUser(eventsClient: EventsClient, token: string): Promise<void> {
        const usernameEl = document.getElementById('mm-user-admin-username') as HTMLInputElement | null;
        const displayNameEl = document.getElementById('mm-user-admin-display-name') as HTMLInputElement | null;
        const createPasswordEl = document.getElementById('mm-user-admin-create-password') as HTMLInputElement | null;
        const isAdminEl = document.getElementById('mm-user-admin-is-admin') as HTMLInputElement | null;
        const isEntertainerEl = document.getElementById('mm-user-admin-is-entertainer') as HTMLInputElement | null;

        if (!usernameEl || !displayNameEl || !createPasswordEl || !isAdminEl || !isEntertainerEl) return;

        const username = usernameEl.value.trim();
        const display_name = displayNameEl.value.trim() || null;
        const password = createPasswordEl.value;
        const is_user_admin = !!isAdminEl.checked;
        const is_entertainer = !!isEntertainerEl.checked;

        if (!username) {
            this.setFormStatus(USER_ADMIN_MENU_LABELS.usernameRequired, 'error');
            return;
        }
        if (password.length < 8) {
            this.setFormStatus('Password must be at least 8 characters.', 'error');
            return;
        }

        this.setFormStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/admin/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ username, display_name, password, is_user_admin, is_entertainer }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? USER_ADMIN_MENU_LABELS.failedToCreateUser;
                this.setFormStatus(msg, 'error');
                return;
            }

            const created = (data as { user?: AdminUser }).user;
            this.setFormStatus(`User created: ${created?.username ?? username}`, 'success');
            this.clearSelection();
            await this.loadUsers(eventsClient, token);
        } catch (err) {
            Logger.error(MODULE, 'Error creating user', err);
            this.setFormStatus(USER_ADMIN_MENU_LABELS.failedToCreateUser, 'error');
        }
    }

    private async updateSelectedUser(eventsClient: EventsClient, token: string): Promise<void> {
        if (!this.selectedUserId) {
            this.setFormStatus('Select a user to update.', 'error');
            return;
        }

        const usernameEl = document.getElementById('mm-user-admin-username') as HTMLInputElement | null;
        const displayNameEl = document.getElementById('mm-user-admin-display-name') as HTMLInputElement | null;
        const isAdminEl = document.getElementById('mm-user-admin-is-admin') as HTMLInputElement | null;
        const isEntertainerEl = document.getElementById('mm-user-admin-is-entertainer') as HTMLInputElement | null;
        if (!usernameEl || !displayNameEl || !isAdminEl || !isEntertainerEl) return;

        const username = usernameEl.value.trim();
        const display_name = displayNameEl.value.trim() || null;
        const is_user_admin = !!isAdminEl.checked;
        const is_entertainer = !!isEntertainerEl.checked;

        if (!username) {
            this.setFormStatus(USER_ADMIN_MENU_LABELS.usernameRequired, 'error');
            return;
        }

        this.setFormStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(this.selectedUserId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ username, display_name, is_user_admin, is_entertainer }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? 'Failed to update user.';
                this.setFormStatus(msg, 'error');
                return;
            }

            const updatedUser = (data as { user?: Partial<AdminUser> }).user;
            if (updatedUser && typeof updatedUser.is_entertainer !== 'boolean') {
                this.setFormStatus(
                    'Update returned without is_entertainer. Server appears outdated; deploy latest rest-server + DB migration 004.',
                    'error',
                );
                return;
            }

            this.setFormStatus('User updated successfully.', 'success');
            await this.loadUsers(eventsClient, token);
        } catch (err) {
            Logger.error(MODULE, 'Error updating user', err);
            this.setFormStatus('Error updating user.', 'error');
        }
    }

    private async setSelectedUserPassword(eventsClient: EventsClient, token: string): Promise<void> {
        if (!this.selectedUserId) {
            this.setPasswordStatus('Select a user before setting a password.', 'error');
            return;
        }

        const newPasswordEl = document.getElementById('mm-user-admin-new-password') as HTMLInputElement | null;
        if (!newPasswordEl) return;

        const new_password = newPasswordEl.value;
        if (new_password.length < 8) {
            this.setPasswordStatus('New password must be at least 8 characters.', 'error');
            return;
        }

        this.setPasswordStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(this.selectedUserId)}/password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ new_password }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? 'Failed to update password.';
                this.setPasswordStatus(msg, 'error');
                return;
            }

            newPasswordEl.value = '';
            this.setPasswordStatus('Password updated successfully.', 'success');
        } catch (err) {
            Logger.error(MODULE, 'Error setting user password', err);
            this.setPasswordStatus('Error updating password.', 'error');
        }
    }

    private async depositSelectedUserTokens(eventsClient: EventsClient, token: string): Promise<void> {
        if (!this.selectedUserId) {
            this.setFormStatus('Select a user before depositing tokens.', 'error');
            return;
        }

        const depositAmountEl = document.getElementById('mm-user-admin-deposit-amount') as HTMLInputElement | null;
        if (!depositAmountEl) return;

        const amount = Number(depositAmountEl.value || '0');
        if (!Number.isInteger(amount) || amount <= 0) {
            this.setFormStatus('Deposit amount must be a positive whole number.', 'error');
            return;
        }

        try {
            const result = await this.depositTokensForUser(eventsClient, token, this.selectedUserId, amount);
            if (!result.ok) {
                this.setFormStatus(result.error ?? 'Failed to deposit tokens.', 'error');
                return;
            }

            const targetBalance = Number(result.target_balance ?? NaN);
            if (Number.isFinite(targetBalance)) {
                const tokenBalanceEl = document.getElementById('mm-user-admin-token-balance');
                if (tokenBalanceEl) tokenBalanceEl.textContent = String(targetBalance);
            }

            depositAmountEl.value = '';
            this.setFormStatus(`Deposited ${amount} token(s).`, 'success');
            await this.loadUsers(eventsClient, token);
        } catch (err) {
            Logger.error(MODULE, 'Error depositing tokens', err);
            this.setFormStatus('Error depositing tokens.', 'error');
        }
    }

    private async depositTokensForUser(
        eventsClient: EventsClient,
        token: string,
        userId: string,
        amount: number,
    ): Promise<{ ok: boolean; error?: string; target_balance?: number }> {
        const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/token-deposits`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ amount }),
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
            const msg = (data as { error?: string }).error ?? 'Failed to deposit tokens.';
            return { ok: false, error: msg };
        }

        const targetBalance = Number((data as { target_balance?: number }).target_balance ?? NaN);
        return {
            ok: true,
            target_balance: Number.isFinite(targetBalance) ? targetBalance : undefined,
        };
    }

    private setFormStatus(message: string, type: '' | 'success' | 'error'): void {
        this.setStatus('mm-user-admin-form-status', message, type);
    }

    private setPasswordStatus(message: string, type: '' | 'success' | 'error'): void {
        this.setStatus('mm-user-admin-password-status', message, type);
    }

    private setStatus(id: string, message: string, type: '' | 'success' | 'error'): void {
        const statusEl = document.getElementById(id);
        if (!statusEl) return;
        statusEl.textContent = message;
        statusEl.className = `mm-user-admin-status${type ? ` ${type}` : ''}`;
    }
}
